// packages/ui/src/components/BeszelDiskCard.tsx
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '../api/client.js'

interface BeszelApproval {
  id: string
  status: string
  title: string
  body: string
  severity: number
  expires_at: string
  payload: { system: string; pct: number }
  resolved_by_channel?: string
  resolved_by_display?: string
  action_error?: string
  action_attempt_count?: number
}

export function BeszelDiskCard({ approval }: { approval: BeszelApproval }) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)

  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'skipped') =>
      apiRequest<unknown>(`/approvals/${approval.id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ decision }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  })

  const dismiss = useMutation({
    mutationFn: () =>
      apiRequest<unknown>(`/approvals/${approval.id}/dismiss`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  })

  const retry = useMutation({
    mutationFn: () =>
      apiRequest<unknown>(`/approvals/${approval.id}/retry`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  })

  const isPending = approval.status === 'pending'
  const isInvestigating = approval.status === 'investigating'
  const isFailed = approval.status === 'failed'
  const isRunning = approval.status === 'running'
  const isDone = approval.status === 'done'
  const isResolved = ['skipped', 'timeout', 'dismissed', 'approved', 'running', 'done', 'failed'].includes(approval.status)

  return (
    <div className="border rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-semibold">🖥️ {approval.payload.system}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${approval.severity >= 90 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
            {approval.payload.pct}% disk
          </span>
        </div>
        {isResolved && (
          <span className="text-xs text-gray-500">
            {approval.status} · {approval.resolved_by_channel}
          </span>
        )}
      </div>

      {isInvestigating && (
        <div className="text-sm text-gray-500 flex items-center gap-1">
          <span className="animate-spin">⏳</span> Investigating...
        </div>
      )}

      {(isPending || isResolved) && approval.body && (
        <div>
          <button onClick={() => setExpanded(e => !e)} className="text-xs text-blue-600">
            {expanded ? 'Hide findings ▲' : 'Show findings ▼'}
          </button>
          {expanded && (
            <pre className="text-xs bg-gray-50 p-2 mt-1 rounded overflow-auto max-h-48 whitespace-pre-wrap">
              {approval.body}
            </pre>
          )}
        </div>
      )}

      {isRunning && (
        <div className="text-sm text-blue-600 flex items-center gap-1">
          <span className="animate-spin">⚙️</span> Running cleanup... (attempt {approval.action_attempt_count})
        </div>
      )}

      {isDone && (
        <div className="text-sm text-green-600">✅ Cleanup complete</div>
      )}

      {isFailed && (
        <div className="space-y-1">
          <div className="text-sm text-red-600">❌ Cleanup failed: {approval.action_error}</div>
          <button
            onClick={() => retry.mutate()}
            disabled={retry.isPending}
            className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700 hover:bg-orange-200"
          >
            Retry
          </button>
        </div>
      )}

      {isPending && (
        <div className="flex gap-2">
          <button
            onClick={() => decide.mutate('approved')}
            disabled={decide.isPending}
            className="px-3 py-1 rounded bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50"
          >
            ✅ Approve
          </button>
          <button
            onClick={() => decide.mutate('skipped')}
            disabled={decide.isPending}
            className="px-3 py-1 rounded bg-gray-200 text-gray-700 text-sm hover:bg-gray-300 disabled:opacity-50"
          >
            ❌ Skip
          </button>
          <button
            onClick={() => dismiss.mutate()}
            disabled={dismiss.isPending}
            className="ml-auto text-xs text-gray-400 hover:text-gray-600"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  )
}
